import {startTransition, useCallback, useEffect, useMemo, useState, type ReactNode} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getAuditProgramSection,
    saveAuditProgramSectionPage,
} from '@/services/auditService/auditProgramService';
import AppendixTableView from '@/pages/components/audit-program/appendix/AppendixTableView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';
import type {G0Page} from '@/pages/components/audit-program/g-section/constants';
import {
    PAF06_2_PAGE,
    PAF_PAGE_TITLES,
    PAF_SECTION_INDEX,
    PAF_SECTION_INDEX_LETTER,
    PAF_SECTION_INDEX_TITLE,
    PAF_SECTION_PAGES,
    getPafFormPage,
    getPafProgrammePage,
    getPafTablePage,
    type PafSectionPageCode,
} from '@/pages/components/audit-program/paf-section/constants';
import PafFormView from '@/pages/components/audit-program/paf-section/PafFormView';
import PafLetterView from '@/pages/components/audit-program/paf-section/PafLetterView';

export type PafSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: PafSectionPageCode;
};

const DISPLAY_ONLY_PAGES = new Set<PafSectionPageCode>(['PAF01']);
const SECTION_CODE = 'PAF';

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function PafSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'PAF01',
}: PafSectionViewProps) {
    const [pageKey, setPageKey] = useState<PafSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates(SECTION_CODE);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAuditProgramSection(companyId, annualId, SECTION_CODE);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of PAF_SECTION_PAGES) {
                if (code === 'PAF01') {
                    pages[code] = toStringRecord(data.pages?.[code]);
                    continue;
                }
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load Permanent Audit File';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: PafSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveAuditProgramSectionPage({
                companyId,
                annualId,
                sectionCode: SECTION_CODE,
                pageCode: pageKey,
                fieldData: pageData[pageKey] ?? {},
            });
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const indexRows = useMemo(() => {
        const fromApi = getContent('PAF01') as {rows?: typeof PAF_SECTION_INDEX} | undefined;
        return fromApi?.rows?.length ? fromApi.rows : PAF_SECTION_INDEX;
    }, [getContent]);

    const tabs = useMemo(() => PAF_SECTION_PAGES.map((code) => {
        let content: ReactNode = null;
        if (header) {
            if (code === 'PAF01') {
                content = (
                    <SectionIndexView
                        header={header}
                        refCode="PAF01"
                        sectionLetter={PAF_SECTION_INDEX_LETTER}
                        sectionTitle={PAF_SECTION_INDEX_TITLE}
                        rows={indexRows}
                        onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode as PafSectionPageCode))}
                    />
                );
            } else if (code === 'PAF06.2') {
                content = (
                    <PafLetterView
                        header={header}
                        page={PAF06_2_PAGE}
                        refCode={code}
                        values={pageData[code] ?? {}}
                        onChange={(k, v) => handleFieldChange(code, k, v)}
                    />
                );
            } else {
                const form = getPafFormPage(code);
                const programme = getPafProgrammePage(code);
                const table = getPafTablePage(code);
                if (form) {
                    content = (
                        <PafFormView
                            header={header}
                            page={form}
                            refCode={code}
                            values={pageData[code] ?? {}}
                            onChange={(k, v) => handleFieldChange(code, k, v)}
                        />
                    );
                } else if (programme) {
                    content = (
                        <G0ProgrammeView
                            header={header}
                            page={(getContent(code) ?? programme) as G0Page}
                            refCode={code}
                            values={pageData[code] ?? {}}
                            onChange={(k, v) => handleFieldChange(code, k, v)}
                        />
                    );
                } else if (table) {
                    content = (
                        <AppendixTableView
                            header={header}
                            page={table}
                            refCode={code}
                            values={pageData[code] ?? {}}
                            onChange={(k, v) => handleFieldChange(code, k, v)}
                        />
                    );
                }
            }
        }
        return {
            key: code,
            label: <PageTabLabel code={code} title={PAF_PAGE_TITLES[code]}/>,
            icon: code,
            content,
        };
    }), [header, pageData, handleFieldChange, indexRows, getContent]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const saveDisabled = DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey] || saving;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    PAF — Permanent Audit File
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as PafSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
